import { floatTransformer, integerTransformer, stringTransformer, useRouteQuery } from 'vue-use-route-query';
import type { RouteQueryTransformer } from 'vue-use-route-query';
import { NumberRange } from '~/types/search';
import type { Product } from '~/types/search';
import type { FilterBySubQuery } from '~/types/typesense';

export type ProductFilterMaterializer<T> = (value: T) => FilterBySubQuery<Product>;

export interface ProductFilter<T> {
  readonly key: string;
  readonly data: Ref<T | null>;
  readonly active: ComputedRef<boolean>;
  readonly query: ComputedRef<FilterBySubQuery<Product> | undefined>;
  readonly transformer: RouteQueryTransformer<T>;
}

export class ProductDataFilter<T> implements ProductFilter<T> {
  public readonly data: Ref<T | null>;

  public readonly active = computed(() => !!this.route.query[this.key]?.length);

  public readonly query
    = computed(() => this.data.value === null ? undefined : this.materialize(this.data.value));

  private route = useRoute();

  constructor(
    public readonly key: string,
    public readonly defaultValue: T | null = null,
    public readonly transformer: RouteQueryTransformer<T>,
    public readonly materialize: ProductFilterMaterializer<T>,
  ) {
    const ref = useRouteQuery(key, defaultValue, transformer);

    this.data = computed({
      get: () => ref.value,
      set: (value) => {
        const query = {
          ...this.route.query,
          [this.key]: value !== null ? transformer.toQuery(value) : undefined,
          page: undefined,
        };

        navigateTo({
          path: this.route.path,
          query,
        });
      },
    });
  }
}

export const availabilityTransformer: RouteQueryTransformer<string> = {
  fromQuery: query => ['instock', '103', '104'].includes(query) ? query : undefined,
  toQuery: stringTransformer.toQuery,
};

export const availabilityMaterializer: ProductFilterMaterializer<string> = (value) => {
  const query: FilterBySubQuery<Product> = {};

  if (value == 'instock') {
    query.isInStock = { $eq: true };
  }
  else {
    query.warehouses = {
      $eq: value,
    };
  }

  return query;
};

export const stringArrayTransformer = (separator = ',') => {
  return {
    fromQuery: (query: string) => query.split(separator),
    toQuery: (value: string[]) => value?.join(separator),
  };
};

export const numericRangeTransformer = (separator = ':') => {
  return {
    fromQuery: (query: string) => {
      const parts = query.split(separator);

      if (parts.length != 2) {
        return new NumberRange();
      }

      return new NumberRange(
        parts[0]?.length ? Number(parts[0]) : undefined,
        parts[1]?.length ? Number(parts[1]) : undefined,
      );
    },
    toQuery: (value: NumberRange) =>
      value?.valid() ? `${value.min}${separator}${value.max}` : '',
  };
};

export const numericTransformer = (separator = ':') => {
  return {
    fromQuery: (query: string) => {
      if (!query.includes(separator)) {
        return floatTransformer.fromQuery(query);
      }

      return numericRangeTransformer(separator).fromQuery(query);
    },
    toQuery: (value: number | NumberRange) => {
      if (typeof value === 'number') {
        return floatTransformer.toQuery(value);
      }

      if (value.same()) {
        return floatTransformer.toQuery(value.first());
      }

      return numericRangeTransformer(separator).toQuery(value);
    },
  };
};

export const defaultIntegerTransformer = (defaultValue: number) => ({
  fromQuery: integerTransformer.fromQuery,
  toQuery: (value: number) =>
    value == defaultValue ? undefined : integerTransformer.toQuery(value),
});

export const sortTransformer = (
  sorts: { key: string; value: string; label: string }[],
) => ({
  fromQuery: (query: string) => {
    if (!query) {
      return sorts[0]?.value;
    }

    return sorts.find(s => s.key == query)?.value ?? sorts[0]?.value;
  },
  toQuery: (value: string) => {
    if (!value || value == sorts[0]?.value) {
      return undefined;
    }

    return value
      ? sorts.find(s => s.value == value)?.key ?? sorts[0]?.key
      : sorts[0]?.key;
  },
});
